import yaml from "js-yaml";

export type LayerType = "xyz" | "wms" | "wmts" | "cog" | "mapwarper" | "arcgis-dynamic";

/** Top-level grouping in the picker. Order in CATEGORY_ORDER drives display order. */
export type LayerCategory = "reference" | "imagery" | "historical";

export const CATEGORY_ORDER: LayerCategory[] = ["reference", "imagery", "historical"];
export const CATEGORY_TITLE: Record<LayerCategory, string> = {
  reference: "Reference",
  imagery: "Imagery",
  historical: "Historical maps",
};

export interface CatalogLayer {
  id: string;
  title: string;
  category: LayerCategory;
  /** Number for dated layers (1978, 2023), or "current" for living/refreshed services. */
  year: number | "current";
  type: LayerType;
  url: string;
  attribution: string;
  /** Short descriptor for the picker row, e.g. "refreshed every 1–3 years". */
  vintage?: string;
  bounds?: [[number, number], [number, number]]; // [[south, west],[north, east]]
  /** Optional real-content coverage: NON-OVERLAPPING [[s,w],[n,e]] boxes approximating
   *  where the layer actually has data (grid-derived by pipelines/coverage_probe.py;
   *  written only when real coverage is much smaller than the rectangular `bounds`,
   *  e.g. statewide services whose bbox corners are empty, county COGs white-padded
   *  past the county line). When present, viewport relevance + date-cell attachment
   *  use these instead of `bounds`; `bounds` remains the fitBounds / z-order /
   *  regional-classification primitive. */
  coverage?: [[number, number], [number, number]][];
  default_opacity?: number;
  /** Highest zoom level at which the source has tiles. */
  max_zoom?: number;
  /** Lowest zoom level at which the source has tiles. */
  min_zoom?: number;
  /** Lowest zoom the tile CACHE has REAL tiles at (below it, blank placeholders).
   *  Leaflet downscales from this floor for display zooms down to min_zoom. */
  min_native_zoom?: number;
  subdomains?: string[];
  /** Tile pixel size when not 256 (e.g. 512 for MapTiler raster tiles). */
  tile_size?: number;
  /** cog-only: render near-pure-white (>=250 all channels) as transparent — for
   *  merged map/atlas/Sanborn COGs whose inter-sheet gaps & off-quad margins are
   *  white-filled, so the basemap shows through instead of opaque white. */
  transparent_white?: boolean;

  // wms-only
  wms_layer?: string;
  wms_format?: string;
  wms_transparent?: boolean;

  // wmts-only — for KVP GetTile construction. Skip all of these if `url` is
  // already a RESTful {z}/{x}/{y} tile template (then wmts behaves like xyz).
  wmts_layer?: string;            // WMTS LAYER identifier
  wmts_style?: string;            // STYLE (default "default")
  wmts_tilematrixset?: string;    // TILEMATRIXSET (default "GoogleMapsCompatible")
  wmts_format?: string;           // tile image format (default "image/png")

  // arcgis-dynamic-only: "4326" to send the tile bbox as lon/lat for services
  // (CAGIS, CT ECO, …) that only render with bboxSR=4326. Default 3857.
  arcgis_bbox_sr?: string;
  // arcgis-dynamic-only: extra query-string params for the /export call.
  // e.g. {"layers": "show:6"} to render a specific layer in a multi-layer MapServer.
  arcgis_export_params?: Record<string, string>;

  // --- layer grouping (UI-level "one unified layer for an area/date") ---
  // Member layers (atlas plates, Sanborn volumes covering different parts of one
  // city at one date) tagged with the same `group_id` collapse into ONE picker
  // entry. Activating it adds all members as a single logical layer (one opacity,
  // one active slot, one swipe side) — no re-merge into a COG needed. Use for a
  // HANDFUL of layers; many-sheet sets should still be merged into a COG for perf.
  group_id?: string;
  group_title?: string;        // display title for the collapsed group entry
  /** Runtime-only: present on the synthetic group unit, the layers it fans out to. */
  members?: CatalogLayer[];
}

export interface Catalog {
  // Geometry (each layer's `bounds`) is the only locality primitive — there is no
  // `regions` registry. Locality is derived from where layers actually are.
  layers: CatalogLayer[];
}

export async function loadCatalog(url = "/catalog.yaml"): Promise<Catalog> {
  const res = await fetch(url, { cache: "no-cache" });
  if (!res.ok) throw new Error(`Failed to load catalog: ${res.status}`);
  const text = await res.text();
  const parsed = yaml.load(text) as Partial<Catalog>;
  if (!parsed || !Array.isArray(parsed.layers)) {
    throw new Error("catalog.yaml: must define a `layers` array");
  }
  return parsed as Catalog;
}
